import React, { useContext, useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import InputLabel from "@mui/material/InputLabel";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import { InputProps } from "../Interfaces/InputProps";
import { InputType } from "../Enums/InputType";
import CallTableViewModel from "../../../ViewModels/CallTableViewModel";
import { handleAutoCompleteChange } from "../Utility/FilterChangeHandlers";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import Chip from "@mui/material/Chip";
import QAComplaintsFiltersViewModel from "./ViewModels/QAComplaintsFiltersViewModel";
import { MsalContext } from "../../../Contexts/MsalContext";
import DateTimeComponent from "../DateTimeFilters/DateTimeComponent";
import DateTimeFiltersViewModel from "../DateTimeFilters/ViewModels/DateTimeFiltersViewModel";
import FormControlLabel from "@mui/material/FormControlLabel";

interface IQAComplaintsFiltersDialogProps {
    open: boolean
    setOpen: (value: boolean) => void
    isAgent: boolean;
    callTableViewModel: CallTableViewModel
    setCallTableViewModel: (value: CallTableViewModel) => void
}

export default function QAComplaintsFiltersDialog({ open, setOpen, callTableViewModel, setCallTableViewModel, isAgent }: IQAComplaintsFiltersDialogProps) {
    const { msalFetch } = useContext(MsalContext);
    const [qaComplaintsFilters, setQAComplaintsFilters] = useState<QAComplaintsFiltersViewModel>(callTableViewModel.filters.qaComplaintsFilters);
    const [outcome, setOutcome] = useState<string[]>([]);
    const [qaComplaint, setQAComplaint] = useState<string[]>([]);
    const [qaComplaintType, setQAComplaintType] = useState<string[]>([]);
    const [originator, setOriginator] = useState<string[]>([]);

    useEffect(() => {
        if (open) {
            setQAComplaintsFilters(callTableViewModel.filters.qaComplaintsFilters);
        }
    }, [open])

    useEffect(() => {
        setQAComplaintsFilters({
            ...qaComplaintsFilters,
            qaComplaint: [],
        });

        if (qaComplaintsFilters.qaComplaintType !== '')
            msalFetch(`api/Complaints/GetComplaintCategoryDropdown?complaintType=${qaComplaintsFilters.qaComplaintType}&all=${false}`)
                .then(response => response.json())
                .then((data: any) =>
                    setQAComplaint(data)
                );

    }, [qaComplaintsFilters.qaComplaintType])

    useEffect(() => {
        if (callTableViewModel.filters.client) {
            msalFetch(`api/Complaints/GetOutcomesDropdown?all=${false}`)
                .then(response => response.json())
                .then((data: any) =>
                    setOutcome(data)
                );

            msalFetch(`api/Complaints/GetComplaintTypeDropdown?all=${false}`)
                .then(response => response.json())
                .then((data: any) =>
                    setQAComplaintType(data)
            );

            msalFetch(`api/Complaints/GetActionOriginDropdown`)
                .then(response => response.json())
                .then((data: any) =>
                    setOriginator(data)
                );

        }
    }, [callTableViewModel.filters.client])

    /*           
            get('Complaints', `GetComplaintTypeDropdown?all=false`, (data) => setQAComplaintType(data));
            get('Complaints', `GetOutcomesDropdown`, (data) => setOutcome(data));
            get('Complaints', `GetComplaintCategoryDropdown?complaintType=${qaComplaintsFilters.qaComplaintType}`, (data) => setQAComplaint(data));
     */

    const inputProps: InputProps[] = [
        {
            name: 'qaComplaintType',
            label: 'Action Type',
            value: qaComplaintsFilters.qaComplaintType,
            options: qaComplaintType,
            disabled: false,
            type: InputType.AutoComplete
        },
        {
            name: 'qaComplaint',
            label: 'Category',
            value: qaComplaintsFilters.qaComplaint,
            options: qaComplaint,
            disabled: qaComplaintsFilters.qaComplaintType === '',
            type: InputType.AutoCompleteMultiple
        },
        {
            name: 'originator',
            label: 'Originator',
            value: qaComplaintsFilters.originator,
            options: originator,
            disabled: false,
            type: InputType.AutoCompleteMultiple
        },
        {
            name: 'outcome',
            label: 'Outcome',
            value: qaComplaintsFilters.outcome,
            options: outcome,
            disabled: false,
            type: InputType.AutoCompleteMultiple
        },
    ];

    const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
    const checkedIcon = <CheckBoxIcon fontSize="small" />;

    const getInput = (inputProps: InputProps) => {
        return (
            <Grid key={inputProps.name} size={12}>
                {inputProps.type === InputType.AutoCompleteMultiple &&
                    !inputProps.disabled &&
                    inputProps.options!.length > 0 &&
                    <React.Fragment>
                        <InputLabel>{inputProps.label}</InputLabel>
                        <Autocomplete
                            multiple
                            fullWidth
                            options={inputProps.options!}
                            disabled={inputProps.disabled}
                            disableCloseOnSelect
                            getOptionLabel={(option) => option}
                            value={inputProps.value as string[]}
                            onChange={
                                (_event: any, newValue: string[]) => { handleAutoCompleteChange(inputProps.name, newValue, setQAComplaintsFilters) }}
                            limitTags={3}
                            renderOption={(props, option, { selected }) => {
                                const { ...optionProps } = props;
                                return (
                                    <li {...optionProps}>
                                        <Checkbox
                                            icon={icon}
                                            checkedIcon={checkedIcon}
                                            style={{ marginRight: 8 }}
                                            checked={selected}
                                        />
                                        {option}
                                    </li>
                                );
                            }}
                            renderInput={(params) => (
                                <TextField {...params} fullWidth />
                            )}
                            renderTags={(value, getTagProps) => {
                                const numTags = value.length;
                                const limitTags = 1;
                                return (
                                    <>
                                        {value.slice(0, limitTags).map((option, index) => (
                                            <Chip
                                                {...getTagProps({ index })}
                                                key={index}
                                                label={option}
                                            />
                                        ))}

                                        {numTags > limitTags && ` +${numTags - limitTags}`}
                                    </>
                                );
                            }}
                        />
                    </React.Fragment>
                }
                {inputProps.type === InputType.AutoComplete &&
                    <React.Fragment>
                        <InputLabel>{inputProps.label}</InputLabel>
                        <Autocomplete
                            onChange={(_e: any, newValue: string) => handleAutoCompleteChange(inputProps.name, newValue, setQAComplaintsFilters)}
                            options={inputProps.options!}
                            disabled={inputProps.disabled}
                            value={inputProps.value as string}
                            fullWidth
                            renderInput={(params) => <TextField {...params} />}
                        />
                    </React.Fragment>
                }
            </Grid >)
    }

    const confirm = () => {
        const newCallTableViewModel = { ...callTableViewModel };

        newCallTableViewModel.filters.qaComplaintsFilters = qaComplaintsFilters;

        setCallTableViewModel({
            ...newCallTableViewModel
        })

        setOpen(false);
    }

    function updateLoggedDateTime(value: DateTimeFiltersViewModel) {
        setQAComplaintsFilters({
            ...qaComplaintsFilters,
            loggedDateFilter: value
        });
    }

    return (
        <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
            <DialogTitle>QA Actions Filters</DialogTitle>
            <DialogContent>
                <Grid container height="100%">
                    {
                        inputProps.map(props => getInput(props))
                    }
                    <Grid size={12}>
                        <FormControlLabel control={
                            <Checkbox
                                checked={qaComplaintsFilters.searchLoggedDate}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                    setQAComplaintsFilters({
                                        ...qaComplaintsFilters,
                                        searchLoggedDate: event.target.checked
                                    })}
                            />}
                            label="Search Logged Date Time"
                        />
                    </Grid>
                    {qaComplaintsFilters.searchLoggedDate &&
                        <Grid size={12}>
                            <DateTimeComponent dateTimeFilters={qaComplaintsFilters.loggedDateFilter} setDateTimeFilters={updateLoggedDateTime} isAgent={isAgent} />
                        </Grid>
                    }
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setOpen(false)}>
                    Cancel
                </Button>
                <Button onClick={confirm}>
                    Confirm
                </Button>
            </DialogActions>
        </Dialog>
    )
}